import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Icon } from '@/components/common/Icon';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { cn } from '@/lib/utils';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useClientsQuery } from '@/hooks/useClientsQuery';
import { useCreateBroadcastMutation, useSendBroadcastMutation } from '@/hooks/useBroadcasts';
import { useNotificationProvidersQuery } from '@/hooks/useNotificationProviders';
import type { BroadcastAudienceKind, BroadcastPurpose } from '@/api/broadcasts/broadcastsApi.types';
import { buildChannelSummary } from './buildChannelSummary';
import { BroadcastPurposeSelector } from './BroadcastPurposeSelector';
import { ComposeBroadcastPreview } from './ComposeBroadcastPreview';
import { useMarketingConsentedCount } from './useMarketingConsentedCount';

const SMS_CHANNEL = 'sms';

interface SelectedClient {
  id: string;
  fullName: string;
}

function ChannelToggle({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      className={cn(
        'px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
        active
          ? 'bg-zooprimary text-zoowhite border-zooprimary'
          : 'bg-zoowhite text-zoomuted border-zooborder hover:text-zooindigo'
      )}
    >
      {label}
    </button>
  );
}

export function ComposeBroadcast() {
  const { t, i18n } = useTranslation('broadcasts');
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [inApp, setInApp] = useState(true);
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(false);
  const [audienceKind, setAudienceKind] = useState<BroadcastAudienceKind>('all_active_clients');
  const [purpose, setPurpose] = useState<BroadcastPurpose>('service');
  const [selectedClients, setSelectedClients] = useState<SelectedClient[]>([]);
  const [search, setSearch] = useState('');
  const [scheduleLater, setScheduleLater] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MILLISECONDS);
  const clientsQuery = useClientsQuery(1, debouncedSearch, 'active');
  const activeClientsCountQuery = useClientsQuery(1, '', 'active');
  const createMutation = useCreateBroadcastMutation();
  const sendMutation = useSendBroadcastMutation();
  const notificationProvidersQuery = useNotificationProvidersQuery();

  const smsProviderStatus =
    notificationProvidersQuery.data === undefined
      ? undefined
      : notificationProvidersQuery.data.providers.find(function matchSmsChannel(provider) {
          return provider.channel === SMS_CHANNEL;
        });
  const isSmsChannelAvailable =
    smsProviderStatus !== undefined &&
    smsProviderStatus.isConfigured === true &&
    smsProviderStatus.isEnabled === true;

  useEffect(
    function disableSmsWhenUnavailable() {
      if (isSmsChannelAvailable === false && sms === true) {
        setSms(false);
      }
    },
    [isSmsChannelAvailable, sms]
  );

  const allActiveCount =
    activeClientsCountQuery.data === undefined ? 0 : activeClientsCountQuery.data.totalItems;
  const isBusy = createMutation.isPending || sendMutation.isPending;
  const selectedIds = useMemo(
    function collectIds() {
      return selectedClients.map(function pick(client) {
        return client.id;
      });
    },
    [selectedClients]
  );

  const audienceCount =
    audienceKind === 'selected_clients' ? selectedClients.length : allActiveCount;
  const consentedCount = useMarketingConsentedCount(purpose, audienceKind, selectedIds);
  const recipientCount =
    purpose === 'marketing' && consentedCount !== null ? consentedCount : audienceCount;

  const activeLanguage =
    i18n.resolvedLanguage === undefined ? i18n.language : i18n.resolvedLanguage;
  const selectedChannelLabels = useMemo(
    function collectChannelLabels() {
      const labels: string[] = [];
      if (inApp === true) {
        labels.push(t('compose.channelNameInApp'));
      }
      if (email === true) {
        labels.push(t('compose.channelNameEmail'));
      }
      if (sms === true) {
        labels.push(t('compose.channelNameSms'));
      }
      return labels;
    },
    [inApp, email, sms, t]
  );
  const channelSummary = buildChannelSummary(selectedChannelLabels, activeLanguage);

  const scheduledForIso =
    scheduleLater === true && scheduledFor.length > 0 ? new Date(scheduledFor).toISOString() : null;

  const confirmQuestion =
    scheduledForIso === null
      ? t('compose.confirmSendDescription', { count: recipientCount, channels: channelSummary })
      : t('compose.confirmScheduleDescription', {
          count: recipientCount,
          channels: channelSummary,
          date: formatIsoDateTimeToTimestamp(scheduledForIso),
        });
  const confirmDescription = confirmQuestion + ' ' + t('compose.confirmReachNote');

  function toggleClient(client: SelectedClient) {
    setSelectedClients(function update(current) {
      const exists = current.some(function match(entry) {
        return entry.id === client.id;
      });
      if (exists) {
        return current.filter(function keep(entry) {
          return entry.id !== client.id;
        });
      }
      return [...current, client];
    });
  }

  function openConfirmation(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (!(inApp || email || sms)) {
      setErrorMessage(t('compose.noChannel'));
      return;
    }
    if (audienceKind === 'selected_clients' && selectedClients.length === 0) {
      setErrorMessage(t('compose.noAudience'));
      return;
    }
    setIsConfirmOpen(true);
  }

  async function confirmSend() {
    try {
      const created = await createMutation.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        channels: { inApp, email, sms },
        audienceKind,
        purpose,
        selectedClientIds: audienceKind === 'selected_clients' ? selectedIds : null,
      });
      await sendMutation.mutateAsync({ broadcastId: created.id, scheduledFor: scheduledForIso });
      showSuccessNotification(
        scheduledForIso === null ? t('compose.sent') : t('compose.scheduled')
      );
      setIsConfirmOpen(false);
      navigate(APPLICATION_ROUTES.workspaceBroadcasts);
    } catch (sendError) {
      const backendMessage = resolveApiErrorMessage(sendError);
      showErrorNotification(backendMessage.length > 0 ? backendMessage : t('compose.error'));
      setIsConfirmOpen(false);
    }
  }

  const searchResults = clientsQuery.data === undefined ? [] : clientsQuery.data.items;

  return (
    <PageContainer width="full">
      <div className="mb-6">
        <button
          type="button"
          onClick={function back() {
            navigate(APPLICATION_ROUTES.workspaceBroadcasts);
          }}
          className="text-sm font-semibold text-zoomuted hover:text-zooindigo flex items-center gap-1"
        >
          <Icon name="ArrowLeft" /> {t('detail.back')}
        </button>
        <h1 className="text-2xl font-bold text-zooindigo mt-3">{t('compose.title')}</h1>
      </div>

      <form onSubmit={openConfirmation} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card padding="lg" className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-title">{t('compose.titleLabel')}</Label>
            <Input
              id="broadcast-title"
              required
              value={title}
              placeholder={t('compose.titlePlaceholder')}
              onChange={function updateTitle(changeEvent) {
                setTitle(changeEvent.target.value);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-body">{t('compose.bodyLabel')}</Label>
            <Textarea
              id="broadcast-body"
              required
              rows={8}
              value={body}
              placeholder={t('compose.bodyPlaceholder')}
              onChange={function updateBody(changeEvent) {
                setBody(changeEvent.target.value);
              }}
            />
          </div>
        </Card>

        <Card padding="lg" className="space-y-5">
          <BroadcastPurposeSelector
            purpose={purpose}
            onChange={setPurpose}
            audienceCount={audienceCount}
            consentedCount={consentedCount}
          />
          <div className="space-y-2">
            <Label>{t('compose.channelsLabel')}</Label>
            <div className="flex flex-wrap gap-2">
              <ChannelToggle
                label={t('compose.channelInApp')}
                active={inApp}
                onToggle={function toggle() {
                  setInApp(!inApp);
                }}
              />
              <ChannelToggle
                label={t('compose.channelEmail')}
                active={email}
                onToggle={function toggle() {
                  setEmail(!email);
                }}
              />
              {isSmsChannelAvailable === true ? (
                <ChannelToggle
                  label={t('compose.channelSms')}
                  active={sms}
                  onToggle={function toggle() {
                    setSms(!sms);
                  }}
                />
              ) : null}
            </div>
            {sms === true ? (
              <p className="text-[11px] text-zoomuted">{t('compose.smsHint')}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>{t('compose.audienceLabel')}</Label>
            <div className="flex flex-wrap gap-2">
              <ChannelToggle
                label={t('compose.audienceAll')}
                active={audienceKind === 'all_active_clients'}
                onToggle={function pickAll() {
                  setAudienceKind('all_active_clients');
                }}
              />
              <ChannelToggle
                label={t('compose.audienceSelected')}
                active={audienceKind === 'selected_clients'}
                onToggle={function pickSelected() {
                  setAudienceKind('selected_clients');
                }}
              />
            </div>
            <p className="text-xs text-zooprimary font-semibold">
              {t('compose.audiencePreview', { count: recipientCount })}
            </p>
          </div>

          {audienceKind === 'selected_clients' ? (
            <div className="space-y-2">
              <Input
                value={search}
                placeholder={t('compose.searchClients')}
                onChange={function updateSearch(changeEvent) {
                  setSearch(changeEvent.target.value);
                }}
              />
              {search.trim().length > 0 ? (
                <ul className="max-h-40 overflow-y-auto rounded-xl border border-zooborder divide-y divide-zooborder">
                  {searchResults.map(function renderResult(client) {
                    const isSelected = selectedIds.includes(client.id);
                    return (
                      <li key={client.id}>
                        <button
                          type="button"
                          onClick={function pick() {
                            toggleClient({ id: client.id, fullName: client.fullName });
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-zoovpalelavender"
                        >
                          <span className="text-zooindigo truncate">{client.fullName}</span>
                          {isSelected === true ? (
                            <Icon name="CheckCircle" className="text-zoosuccesstext" />
                          ) : (
                            <span className="text-xs font-semibold text-zooprimary">
                              {t('compose.add')}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {selectedClients.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {selectedClients.map(function renderChip(client) {
                    return (
                      <button
                        key={client.id}
                        type="button"
                        onClick={function remove() {
                          toggleClient(client);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zoovpalelavender text-xs font-medium text-zooindigo"
                      >
                        {client.fullName}
                        <Icon name="X" className="text-[10px]" />
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-2 border-t border-zooborder pt-4">
            <label className="flex items-center gap-2 text-sm text-zooindigo">
              <input
                type="checkbox"
                checked={scheduleLater}
                onChange={function toggleSchedule(changeEvent) {
                  setScheduleLater(changeEvent.target.checked);
                }}
              />
              {t('compose.schedule')}
            </label>
            {scheduleLater === true ? (
              <div className="space-y-1.5">
                <Label htmlFor="broadcast-schedule">{t('compose.scheduleLabel')}</Label>
                <input
                  id="broadcast-schedule"
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={function updateSchedule(changeEvent) {
                    setScheduledFor(changeEvent.target.value);
                  }}
                  className="w-full rounded-xl border border-zooborder px-3 py-2 text-sm"
                />
              </div>
            ) : null}
          </div>

          {errorMessage.length > 0 ? (
            <div
              role="alert"
              className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
            >
              <Icon name="AlertCircle" />
              {errorMessage}
            </div>
          ) : null}

          <Button type="submit" size="large" className="w-full" isLoading={isBusy}>
            {isBusy ? null : <Icon name="Send" />}
            {scheduleLater === true ? t('compose.schedule') : t('compose.sendNow')}
          </Button>
        </Card>

        <div className="lg:col-span-2">
          <ComposeBroadcastPreview title={title} body={body} />
        </div>
      </form>

      {isConfirmOpen === true ? (
        <ConfirmDialog
          title={t('compose.confirmTitle')}
          description={confirmDescription}
          confirmLabel={
            scheduleLater === true
              ? t('compose.confirmScheduleAction')
              : t('compose.confirmSendAction')
          }
          iconName="Send"
          isConfirming={isBusy}
          onConfirm={function onConfirmSend() {
            void confirmSend();
          }}
          onCancel={function onCancelSend() {
            setIsConfirmOpen(false);
          }}
        />
      ) : null}
    </PageContainer>
  );
}
